import {
  TextureLoader,
  BufferGeometry,
  BufferAttribute,
  Points,
  ShaderMaterial,
  Object3D,
  AdditiveBlending,
  MeshDepthMaterial,
  Vector2,
  Vector3,
  MeshBasicMaterial,
  Color,
  Box3
} from 'three'

import { randomRange } from '@/utils/Maths'

import glsl from 'glslify'

export class Particles extends Object3D {
  constructor ({
    color = 0xffffff,
    opacity = 1,
    count = 200,
    size = new Box3(new Vector3(-50, -50, -50), new Vector3(50, 50, 50) ),
    speed = new Vector2(150, 0),
    particleSize = 15
  } = {}) {
    super({ name: 'Particles' })

    this.size = size
    this.count = count
    this.color = new Color(color)
    this.geometry = new BufferGeometry()
    this.vertices = new Float32Array(count * 3)
    this.progress = new Float32Array(count)

    const sizeVector = new Vector3()
    const centerVector = new Vector3()
    this.size.getSize(sizeVector)
    this.size.getCenter(centerVector)

    this.uniforms = {
      uColor: { type: 'f', value: this.color },
      uTime: { type: 'f', value: 0.0 },
      uSpeed: { type: 'vec2', value: speed },
      uMap: { type: 't', value: null },
      uNoiseTexture: { type: 't', value: null },
      uSize: { type: 'vec3', value: sizeVector },
      uCenter: { type: 'vec3', value: centerVector },
      uParticleSize: { type: 'f', value: particleSize }
    }

    this.setup()
  }

  loadTextures () {
    this.textureParticle = null
    this.textureNoise = null

    return Promise.all([
      new Promise((resolve, reject) => {
        new TextureLoader().load('/images/textures/particle.png', (texture) => {
          this.textureParticle = texture
          resolve()
        })
      }),
      new Promise((resolve, reject) => {
        new TextureLoader().load('/images/textures/perlin-noise.jpg', (texture) => {
          this.textureNoise = texture
          resolve()
        })
      })
    ])
  }

  get opacity () {
    return this.uniforms.uOpacity.value
  }

  set opacity (opacity) {
    this.uniforms.uOpacity.value = opacity
  }

  get particleSize () {
    return this.uniforms.uParticleSize.value
  }

  set particleSize (particleSize) {
    this.uniforms.uParticleSize.value = particleSize
  }

  async setup () {
    await this.loadTextures()

    this.uniforms.uMap.value = this.textureParticle
    this.uniforms.uNoiseTexture.value = this.textureNoise

    for (let i = 0; i < this.count; i++) {
      this.vertices[i * 3] = randomRange(this.size.min.x, this.size.max.x)
      this.vertices[i * 3 + 1] = this.size.min.y
      this.vertices[i * 3 + 2] = randomRange(this.size.min.z, this.size.max.z)

      this.progress[i] = Math.random() * 100
    }

    this.geometry.setAttribute('position', new BufferAttribute(this.vertices, 3))
    this.geometry.setAttribute('progress', new BufferAttribute(this.progress, 1))

    this.material = new ShaderMaterial({
      uniforms: this.uniforms,
      transparent: true,
      depthTest: false,
      blending: AdditiveBlending,
      vertexShader: glsl(require('../shaders/particles.vert').default),
      fragmentShader: glsl(require('../shaders/particles.frag').default)
    })


    this.particleSystem = new Points(this.geometry, this.material)

    this.add(this.particleSystem)
  }

  update (time) {
    this.uniforms.uTime.value = time.elapsed * 0.0001
  }
}
